import { FC } from "react";
import { useTranslation } from "react-i18next";

import { ICustomField } from "@ryde/shared";

import { InputSearch } from "../../../common";
import { CustomTranslationsStyled } from "./custom-translations.styled";
import { TranslationPathAutocomplete } from "./translation-path-autocomplete";
import { TranslationRow } from "./translation-row";
import { useCustomTranslations } from "./use-custom-translations";
import { useI18nKeyIndex } from "./use-i18n-key-index";

export const CustomTranslationsTemplate: FC<ICustomField> = ({ name }) => {
  const { t } = useTranslation();
  const { entries, isLoading, getOriginal } = useI18nKeyIndex();
  const {
    rows,
    filteredRows,
    existingPaths,
    highlightedPath,
    search,
    setSearch,
    addRow,
    updateRow,
    removeRow,
  } = useCustomTranslations(name);

  const hasRows = rows.length > 0;
  const hasFilteredRows = filteredRows.length > 0;

  return (
    <CustomTranslationsStyled>
      <div className="ct-header">
        <div className="ct-header__title-row">
          <h3 className="ct-header__title">
            {t("systemParam.formLabel.customTranslations")}
          </h3>
          {hasRows && <span className="ct-header__count">{rows.length}</span>}
        </div>
        <p className="ct-header__hint">
          {t("systemParam.customTranslations.description")}
        </p>
      </div>

      <div className="ct-picker">
        <span className="ct-picker__label">
          {t("systemParam.customTranslations.addOverride")}
        </span>
        <TranslationPathAutocomplete
          entries={entries}
          isLoading={isLoading}
          existingPaths={existingPaths}
          onPick={entry =>
            addRow({ path: entry.path, en: entry.en, he: entry.he })
          }
        />
      </div>

      {hasRows && (
        <div className="ct-toolbar">
          <div className="ct-toolbar__search">
            <InputSearch
              label={t("general.SEARCH")}
              value={search}
              onSearchType={setSearch}
              onSearchApply={setSearch}
            />
          </div>
        </div>
      )}

      {!hasRows && (
        <div className="ct-empty" data-e2e="custom-translations-empty">
          <span className="ct-empty__title">
            {t("systemParam.customTranslations.emptyTitle")}
          </span>
          <p className="ct-empty__text">
            {t("systemParam.customTranslations.emptyText")}
          </p>
        </div>
      )}

      {hasRows && !hasFilteredRows && (
        <div className="ct-empty" data-e2e="custom-translations-no-results">
          <span className="ct-empty__title">{t("general.NO_RESULTS")}</span>
        </div>
      )}

      {hasFilteredRows && (
        <div className="ct-list">
          {filteredRows.map(({ row, index }) => (
            <TranslationRow
              key={row.path || index}
              row={row}
              index={index}
              original={getOriginal(row.path)}
              isHighlighted={row.path === highlightedPath}
              onChange={updateRow}
              onRemove={removeRow}
            />
          ))}
        </div>
      )}
    </CustomTranslationsStyled>
  );
};
